import { HTMLAttributes, useCallback, useEffect, useMemo, useRef } from 'react'
import mapboxgl, { GeoJSONSource, LngLatBounds } from 'mapbox-gl'

import 'mapbox-gl/dist/mapbox-gl.css'

import { cn } from '@/lib/utils'
import { SalesPoint, SalesPointCategory } from '@/types/sales-network'

// Token Mapbox - in produzione usare variabile d'ambiente
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN

const SOURCE_ID = 'stores-source'
const CLUSTER_LAYER_ID = 'clusters'
const CLUSTER_COUNT_LAYER_ID = 'cluster-count'
const UNCLUSTERED_LAYER_ID = 'unclustered-point'
const SELECTED_MARKER_LAYER_ID = 'selected-marker'
const SELECTED_GLOW_LAYER_ID = 'selected-marker-glow'

type StoreCoordinates = {
  lat: number
  lon: number
}

const toCoordinateNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.')
    if (!normalized) return null

    const parsed = Number(normalized)
    if (Number.isFinite(parsed)) return parsed

    const fallbackParsed = Number.parseFloat(normalized)
    return Number.isFinite(fallbackParsed) ? fallbackParsed : null
  }

  return null
}

const isValidLatitude = (value: number): boolean => value >= -90 && value <= 90

const isValidLongitude = (value: number): boolean =>
  value >= -180 && value <= 180

const getStoreCoordinates = (store: SalesPoint): StoreCoordinates | null => {
  const rawLat = toCoordinateNumber(store?.acf?.place?.lat)
  const rawLon = toCoordinateNumber(store?.acf?.place?.lon)

  if (rawLat === null || rawLon === null) return null

  if (isValidLatitude(rawLat) && isValidLongitude(rawLon)) {
    return { lat: rawLat, lon: rawLon }
  }

  if (isValidLatitude(rawLon) && isValidLongitude(rawLat)) {
    return { lat: rawLon, lon: rawLat }
  }

  return null
}

export type MapBounds = {
  topleft: { lat: number; lon: number }
  bottomright: { lat: number; lon: number }
}

export type FlyToCoords = {
  lng: number
  lat: number
  zoom?: number
  bbox?: [number, number, number, number]
  // Se true e bbox è presente, lo zoom viene ricavato dal bbox
  // ma la mappa viene centrata su lng/lat (marker) invece che sul
  // centro geografico del bbox. Utile per nazioni molto estese (es. USA).
  centerOnMarker?: boolean
}

export type SalesNetworkMapProps = HTMLAttributes<HTMLDivElement> & {
  stores: SalesPoint[]
  allStores?: SalesPoint[]
  categories?: SalesPointCategory[]
  selectedStore?: SalesPoint | null
  onStoreSelect?: (_store: SalesPoint) => void
  onVisibleStoresChange?: (_stores: SalesPoint[]) => void
  initialCenter?: [number, number]
  initialZoom?: number
  initialPitch?: number
  bounds?: MapBounds | null
  flyToCoords?: FlyToCoords | null
  searchPin?: { lat: number; lng: number } | null
  resetKey?: number
}

const SalesNetworkMap = ({
  stores,
  allStores,
  categories = [],
  selectedStore,
  onStoreSelect,
  onVisibleStoresChange,
  initialCenter = [12.4964, 42.8],
  initialZoom = 4,
  initialPitch = 60,
  bounds,
  flyToCoords,
  searchPin,
  resetKey,
  className,
  ...props
}: SalesNetworkMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const isMapLoaded = useRef(false)
  const searchPinMarkerRef = useRef<mapboxgl.Marker | null>(null)
  const isFirstResetRef = useRef(true)

  // Calcola gli store visibili nel viewport corrente (usa allStores per includere tutte le categorie)
  const visibleStoresSource = allStores ?? stores
  const updateVisibleStores = useCallback(() => {
    if (!map.current || !onVisibleStoresChange) return

    const mapBounds = map.current.getBounds()
    if (!mapBounds) return

    const visibleStores = visibleStoresSource.filter((store) => {
      const coordinates = getStoreCoordinates(store)
      if (!coordinates) return false

      return mapBounds.contains([coordinates.lon, coordinates.lat])
    })

    onVisibleStoresChange(visibleStores)
  }, [visibleStoresSource, onVisibleStoresChange])

  const getCategoryColor = useCallback(
    (tipologia: number[]): string => {
      const category = categories.find((cat) => tipologia.includes(cat.term_id))
      switch (category?.slug) {
        case 'flagship':
          return '#D4AF37'
        case 'premium':
          return '#C0C0C0'
        case 'store':
          return '#6B7280'
        default:
          return '#1a1a1a'
      }
    },
    [categories]
  )

  // Converte stores in GeoJSON FeatureCollection
  const geojsonData = useMemo((): GeoJSON.FeatureCollection<GeoJSON.Point> => {
    const features = stores.reduce<GeoJSON.Feature<GeoJSON.Point>[]>(
      (acc, store) => {
        const coordinates = getStoreCoordinates(store)
        if (!coordinates) return acc

        acc.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [coordinates.lon, coordinates.lat]
          },
          properties: {
            id: store.id,
            title: store.title.rendered,
            'store-typology':
              store.taxonomies?.['store-typology']?.map((cat) => cat.term_id) ??
              [],
            color: getCategoryColor(
              store.taxonomies?.['store-typology']?.map((cat) => cat.term_id) ??
                []
            )
          }
        })

        return acc
      },
      []
    )

    return {
      type: 'FeatureCollection',
      features
    }
  }, [stores, getCategoryColor])

  // Flag per sapere se i layer sono già stati creati
  const layersInitialized = useRef(false)

  // Setup iniziale dei layer per clustering (chiamato una sola volta)
  const setupClusterLayers = useCallback(() => {
    if (!map.current || layersInitialized.current) return

    // Aggiungi source con clustering abilitato
    map.current.addSource(SOURCE_ID, {
      type: 'geojson',
      data: geojsonData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    })

    // Layer per i cluster (cerchi)
    map.current.addLayer({
      id: CLUSTER_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#2a2a2a',
          10,
          '#1a1a1a',
          30,
          '#0f0f0f'
        ],
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 30, 30],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff'
      }
    })

    // Layer per il conteggio nei cluster
    map.current.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff'
      }
    })

    // Layer per i punti singoli (non clusterizzati)
    map.current.addLayer({
      id: UNCLUSTERED_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 10,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff'
      }
    })

    // Layer per il bagliore del marker selezionato
    map.current.addLayer({
      id: SELECTED_GLOW_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'id'], ''],
      paint: {
        'circle-color': '#ffffff',
        'circle-radius': 28,
        'circle-opacity': 0.6,
        'circle-blur': 1
      }
    })

    // Layer per il marker selezionato (evidenziato)
    map.current.addLayer({
      id: SELECTED_MARKER_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'id'], ''],
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 12,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff'
      }
    })

    layersInitialized.current = true
  }, [geojsonData])

  // Aggiorna solo i dati GeoJSON quando cambiano gli stores (senza ricreare layer)
  useEffect(() => {
    if (!map.current || !isMapLoaded.current || !layersInitialized.current)
      return

    const source = map.current.getSource(SOURCE_ID) as GeoJSONSource
    if (source) {
      source.setData(geojsonData)
    }
  }, [geojsonData])

  // Gestione click sui cluster
  const handleClusterClick = useCallback(
    (
      e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }
    ) => {
      if (!map.current || !e.features?.[0]) return

      const feature = e.features[0]
      const clusterId = feature.properties?.cluster_id

      const source = map.current.getSource(SOURCE_ID) as GeoJSONSource

      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || !map.current) return

        map.current.easeTo({
          center: (feature.geometry as GeoJSON.Point).coordinates as [
            number,
            number
          ],
          zoom: zoom ?? 10
        })
      })
    },
    []
  )

  // Gestione click sui punti singoli
  const handlePointClick = useCallback(
    (
      e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }
    ) => {
      if (!e.features?.[0]) return

      const storeId = e.features[0].properties?.id
      const store = stores.find((s) => s.id === storeId)

      if (store && onStoreSelect) {
        onStoreSelect(store)
      }
    },
    [stores, onStoreSelect]
  )

  // Inizializza mappa
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: initialCenter,
      zoom: initialZoom,
      minZoom: initialZoom,
      pitch: initialPitch,
      attributionControl: false,
      scrollZoom: false
    })

    map.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'top-right'
    )

    // Abilita scroll zoom solo con CMD/Ctrl premuto
    const handleWheel = (e: WheelEvent) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault()
        map.current?.scrollZoom.enable()
      } else {
        map.current?.scrollZoom.disable()
      }
    }

    const container = mapContainer.current
    container.addEventListener('wheel', handleWheel, { passive: false })

    map.current.on('load', () => {
      isMapLoaded.current = true
    })

    return () => {
      container.removeEventListener('wheel', handleWheel)
      map.current?.remove()
      map.current = null
      isMapLoaded.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Setup layer e gestori eventi quando mappa carica
  useEffect(() => {
    if (!map.current) return

    const setup = () => {
      setupClusterLayers()

      // Click handlers
      map.current?.on('click', CLUSTER_LAYER_ID, handleClusterClick)
      map.current?.on('click', UNCLUSTERED_LAYER_ID, handlePointClick)

      // Cambia cursore su hover
      map.current?.on('mouseenter', CLUSTER_LAYER_ID, () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current?.on('mouseleave', CLUSTER_LAYER_ID, () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })
      map.current?.on('mouseenter', UNCLUSTERED_LAYER_ID, () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current?.on('mouseleave', UNCLUSTERED_LAYER_ID, () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })
    }

    if (isMapLoaded.current) {
      setup()
    } else {
      map.current.on('load', setup)
    }

    return () => {
      map.current?.off('click', CLUSTER_LAYER_ID, handleClusterClick)
      map.current?.off('click', UNCLUSTERED_LAYER_ID, handlePointClick)
    }
  }, [setupClusterLayers, handleClusterClick, handlePointClick])

  // Aggiorna filtro del marker selezionato
  useEffect(() => {
    if (!map.current || !isMapLoaded.current) return

    const filter: mapboxgl.Expression = [
      '==',
      ['get', 'id'],
      selectedStore?.id ?? ''
    ]

    if (map.current.getLayer(SELECTED_GLOW_LAYER_ID)) {
      map.current.setFilter(SELECTED_GLOW_LAYER_ID, filter)
    }
    if (map.current.getLayer(SELECTED_MARKER_LAYER_ID)) {
      map.current.setFilter(SELECTED_MARKER_LAYER_ID, filter)
    }
  }, [selectedStore?.id])

  // Fit bounds quando cambia il paese selezionato
  useEffect(() => {
    if (!bounds || !map.current || !isMapLoaded.current) return
    if (
      !bounds.topleft?.lon ||
      !bounds.topleft?.lat ||
      !bounds.bottomright?.lon ||
      !bounds.bottomright?.lat
    )
      return

    const sw: [number, number] = [bounds.topleft.lon, bounds.bottomright.lat]
    const ne: [number, number] = [bounds.bottomright.lon, bounds.topleft.lat]
    const lngLatBounds = new LngLatBounds(sw, ne)

    map.current.fitBounds(lngLatBounds, {
      padding: 50,
      duration: 1000
    })
  }, [bounds])

  // Fly to coordinates per ricerca indirizzo
  useEffect(() => {
    if (!flyToCoords || !map.current || !isMapLoaded.current) return

    if (flyToCoords.bbox) {
      const [minLng, minLat, maxLng, maxLat] = flyToCoords.bbox
      const lngLatBounds = new LngLatBounds([minLng, minLat], [maxLng, maxLat])

      if (flyToCoords.centerOnMarker) {
        // Ricava lo zoom dal bbox ma centra sul marker (lng/lat).
        const camera = map.current.cameraForBounds(lngLatBounds, {
          padding: 50
        })
        map.current.flyTo({
          center: [flyToCoords.lng, flyToCoords.lat],
          zoom: camera?.zoom ?? flyToCoords.zoom ?? 12,
          duration: 1000
        })
      } else {
        map.current.fitBounds(lngLatBounds, {
          padding: 50,
          duration: 1000
        })
      }
    } else {
      map.current.flyTo({
        center: [flyToCoords.lng, flyToCoords.lat],
        zoom: flyToCoords.zoom ?? 12,
        duration: 1000
      })
    }
  }, [flyToCoords])

  // Marker per il punto di ricerca (indirizzo o geolocalizzazione)
  useEffect(() => {
    searchPinMarkerRef.current?.remove()
    searchPinMarkerRef.current = null

    if (!searchPin || !map.current || !isMapLoaded.current) return

    const el = document.createElement('div')
    el.style.cssText = 'pointer-events:none;line-height:0'
    el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg"
     viewBox="0 0 28.67 34"
     height="36"
     width="30"
     >
      <path
        stroke-miterlimit="128"
        fill="#ffffff"
        stroke="#414750"
        d="M27.82,11.23c-.47-2.01-1.39-3.92-2.67-5.53-1.29-1.61-2.95-2.93-4.8-3.83-1.86-.89-3.92-1.37-5.98-1.37h-.04c-2.05,0-4.1.46-5.95,1.34-1.87.9-3.54,2.22-4.84,3.84-1.29,1.61-2.22,3.53-2.69,5.55-.45,1.97-.47,4.05-.04,6.03.42,1.98,1.29,3.87,2.52,5.48l1.2,1.59c.58.76,1.24,1.48,1.94,2.14l7.22,6.77c.18.17.41.26.64.26s.47-.09.65-.26l7.22-6.77c.7-.66,1.35-1.38,1.93-2.14l1.21-1.59c1.22-1.61,2.1-3.5,2.52-5.48.43-1.98.41-4.06-.04-6.03ZM20.08,14.35c-.2.86-.59,1.66-1.14,2.35-.55.67-1.25,1.22-2.03,1.6-.79.38-1.65.58-2.53.58h-.1c-.87,0-1.73-.2-2.52-.58s-1.48-.93-2.03-1.6c-.55-.69-.94-1.49-1.14-2.35-.2-.85-.2-1.75,0-2.6.19-.85.58-1.64,1.12-2.32.55-.68,1.24-1.23,2.03-1.6l.06-.03c.79-.38,1.66-.57,2.53-.57s1.75.19,2.54.57l.06.03c.79.37,1.48.92,2.02,1.6.55.68.94,1.47,1.13,2.32.2.85.2,1.75,0,2.6Z"
      />
      <path
        fill="#414750"
        d="M20.58,14.46c-.22.94-.64,1.81-1.24,2.56-.6.73-1.36,1.33-2.21,1.74-.86.41-1.79.63-2.75.63h-.11c-.95,0-1.88-.22-2.74-.63s-1.61-1.01-2.21-1.74c-.6-.75-1.02-1.62-1.24-2.56-.22-.92-.22-1.9,0-2.83.21-.92.63-1.78,1.22-2.52.6-.74,1.35-1.34,2.21-1.74l.07-.03c.86-.41,1.81-.62,2.75-.62s1.9.21,2.76.62l.07.03c.86.4,1.61,1,2.2,1.74.6.74,1.02,1.6,1.23,2.52.22.92.22,1.9,0,2.83Z"
      />
    </svg>`

    searchPinMarkerRef.current = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom'
    })
      .setLngLat([searchPin.lng, searchPin.lat])
      .addTo(map.current)
  }, [searchPin])

  // Ascolta moveend per aggiornare gli store visibili (con debounce per ridurre re-render)
  useEffect(() => {
    if (!map.current) return

    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const handleMoveEnd = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        updateVisibleStores()
      }, 150)
    }

    const setupMoveListener = () => {
      map.current?.on('moveend', handleMoveEnd)
      // Calcola subito gli store visibili al primo caricamento
      updateVisibleStores()
    }

    if (isMapLoaded.current) {
      setupMoveListener()
    } else {
      map.current.on('load', setupMoveListener)
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      map.current?.off('moveend', handleMoveEnd)
    }
  }, [updateVisibleStores])

  // Torna alla vista iniziale (mondo) quando resetKey cambia (cambio tab)
  useEffect(() => {
    if (isFirstResetRef.current) {
      isFirstResetRef.current = false
      return
    }
    if (!map.current || !isMapLoaded.current) return
    map.current.flyTo({
      center: initialCenter,
      zoom: initialZoom,
      pitch: initialPitch,
      duration: 1000
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // Riduce il pitch a 0 quando si zooma a livelli ravvicinati
  useEffect(() => {
    if (!map.current) return

    const PITCH_ZOOM_THRESHOLD = 5

    const handleZoomEnd = (e: mapboxgl.MapEventOf<'zoomend'>) => {
      if (!map.current) return
      // Ignora gli zoom programmatici (fitBounds/flyTo da ricerca): il tilt
      // automatico deve reagire solo allo zoom manuale dell'utente.
      const originalEvent = (e as { originalEvent?: unknown }).originalEvent
      if (!originalEvent) return
      const currentZoom = map.current.getZoom()
      const targetPitch = currentZoom > PITCH_ZOOM_THRESHOLD ? 0 : initialPitch
      if (map.current.getPitch() !== targetPitch) {
        map.current.easeTo({ pitch: targetPitch, duration: 500 })
      }
    }

    const setupZoomListener = () => {
      map.current?.on('zoomend', handleZoomEnd)
    }

    if (isMapLoaded.current) {
      setupZoomListener()
    } else {
      map.current.on('load', setupZoomListener)
    }

    return () => {
      map.current?.off('zoomend', handleZoomEnd)
    }
  }, [initialPitch])

  return (
    <div
      ref={mapContainer}
      role="application"
      aria-label="Mappa rete vendita"
      data-lenis-prevent="true"
      className={cn(
        'h-full w-full overflow-hidden rounded-sm',
        'bg-surface-secondary-light dark:bg-surface-secondary-dark',
        className
      )}
      {...props}
    />
  )
}

export default SalesNetworkMap
